import React, { useState } from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Box, TableContainer, Typography, TablePagination, TableSortLabel, IconButton, Tooltip } from '@mui/material';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import auditService, { type ReceiptAudit } from '../../services/auditService';
import '../../css/AuditsTable.css';
import { formatReportType } from '../../utils/formatters/reportTypeFormatter';
import ReceiptAuditStatusIcon from './ReceiptAuditStatusIcon';
import ReceiptNotesDialog from './ReceiptNotesDialog';
import showToast from '../../utils/toastUtils';
import { adminCombineSx, adminTableSx } from '../../styles/adminComponentStyles';
import {
  formatReceiptAuditStatusLabel,
  formatReceiptAuditTimestamp,
  getReceiptAuditShiftDate,
  getReceiptAuditShiftTime,
  isReceiptAuditProcessedStatus,
} from './auditsTableUtils';
import type { AuditSortKey } from '../../types/features/auditListing';
import { adminColors } from '../../styles/adminDesignTokens';

type AuditsTableProps = {
  audits: ReceiptAudit[];
  loading: boolean;
  total: number;
  size: number;
  page: number;
  orderBy: AuditSortKey;
  order: 'asc' | 'desc';
  onRequestSort: (key: AuditSortKey) => void;
  onPageChange: (event: any, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFixOCR: (queueId: string, storeId: string, alreadyProcess: boolean) => void;
  onRevertToReady: (queueId: string) => Promise<void>;
  onReprocess: (queueId: string) => Promise<void>;
  onNotesChanged?: () => void;
};

type DateTimeCellProps = {
  date: string;
  time?: string;
};

type NotePreview = {
  timestamp?: string;
  relativeTimestamp?: string;
  body: string;
};

const NOTE_SEPARATOR_PATTERN = /\n\s*---\s*\n/g;
const NOTE_HEADER_PATTERN = /^\[([^\]]+)\]\s*\n?/;
const NOTE_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2}) ([A-Z]{2,4})$/;
const NOTE_PREVIEW_MAX_LENGTH = 100;
const TIMEZONE_OFFSETS_MINUTES: Record<string, number> = {
  UTC: 0,
  GMT: 0,
  PST: -8 * 60,
  PDT: -7 * 60,
  MST: -7 * 60,
  MDT: -6 * 60,
  CST: -6 * 60,
  CDT: -5 * 60,
  EST: -5 * 60,
  EDT: -4 * 60,
  AKST: -9 * 60,
  AKDT: -8 * 60,
  HST: -10 * 60,
  AST: -4 * 60,
  ADT: -3 * 60,
};

const parseNoteTimestamp = (timestamp?: string): Date | null => {
  if (!timestamp) return null;
  const match = timestamp.match(NOTE_TIMESTAMP_PATTERN);
  if (!match) return null;

  const [, year, month, day, hour, minute, second, timezoneAbbreviation] = match;
  const offsetMinutes = TIMEZONE_OFFSETS_MINUTES[timezoneAbbreviation];
  if (offsetMinutes === undefined) return null;

  const utcMillis = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  ) - offsetMinutes * 60 * 1000;

  return new Date(utcMillis);
};

const formatRelativeTimestamp = (timestamp?: string): string | undefined => {
  const timestampDate = parseNoteTimestamp(timestamp);
  if (!timestampDate) return undefined;

  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - timestampDate.getTime()) / 1000));
  if (elapsedSeconds < 60) return `${elapsedSeconds}s`;

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) return `${elapsedMinutes}m`;

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours}h`;

  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 30) return `${elapsedDays}d`;

  const elapsedMonths = Math.floor(elapsedDays / 30);
  if (elapsedMonths < 12) return `${elapsedMonths}M`;

  return `${Math.floor(elapsedMonths / 12)}y`;
};

const truncateNotePreview = (value: string): string => {
  if (value.length <= NOTE_PREVIEW_MAX_LENGTH) {
    return value;
  }
  return `${value.slice(0, NOTE_PREVIEW_MAX_LENGTH).trimEnd()}...`;
};

const getLatestNotePreview = (reviewNotes?: string | null): NotePreview | null => {
  const entries = (reviewNotes || '')
    .split(NOTE_SEPARATOR_PATTERN)
    .map((entry) => entry.trim())
    .filter(Boolean);

  if (entries.length === 0) {
    return null;
  }

  const latestEntry = entries[entries.length - 1];
  const headerMatch = latestEntry.match(NOTE_HEADER_PATTERN);
  const timestamp = headerMatch?.[1]?.trim();
  const lines = latestEntry
    .replace(NOTE_HEADER_PATTERN, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const bodyLines = lines.length > 1 ? lines.slice(1) : lines;
  const body = truncateNotePreview(bodyLines.join(' ').trim());

  if (!body) {
    return null;
  }

  return { timestamp, relativeTimestamp: formatRelativeTimestamp(timestamp), body };
};

const splitFormattedDateTime = (value: string): DateTimeCellProps => {
  if (!value || value === 'N/A') {
    return { date: 'N/A' };
  }

  const [date = value, time = ''] = value.split(/ (.+)/);
  return {
    date,
    time: time || undefined,
  };
};

const formatShiftDateForDisplay = (value: string): string => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

const formatShiftTimeForDisplay = (value: string): string => {
  if (!value || value === 'N/A' || /\b(?:AM|PM)\b/i.test(value)) {
    return value;
  }

  const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    return value;
  }

  const [, rawHours, minutes, seconds] = match;
  const hours24 = Number(rawHours);
  if (!Number.isInteger(hours24) || hours24 < 0 || hours24 > 23) {
    return value;
  }

  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  const time = seconds ? `${hours12}:${minutes}:${seconds}` : `${hours12}:${minutes}`;
  return `${time} ${suffix}`;
};

const DateTimeCell: React.FC<DateTimeCellProps> = ({ date, time }) => (
  <Box className="at-date-time">
    <Typography variant="body2" className="at-date-text">
      {date}
    </Typography>
    {time && time !== 'N/A' ? (
      <Typography variant="caption" className="at-time-text">
        {time}
      </Typography>
    ) : null}
  </Box>
);

const AuditsTable: React.FC<AuditsTableProps> = ({
  audits,
  loading,
  total,
  size,
  page,
  orderBy,
  order,
  onRequestSort,
  onPageChange,
  onRowsPerPageChange,
  onFixOCR,
  onNotesChanged,
}) => {
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<ReceiptAudit | null>(null);
  const [reviewNotes, setReviewNotes] = useState<string | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  const handleOpenNotes = async (event: React.MouseEvent, audit: ReceiptAudit) => {
    event.stopPropagation();
    const queueId = audit.queue_id || audit.id;
    if (!queueId) return;
    setSelectedAudit(audit);
    setReviewNotes(audit.review_notes || null);
    setNotesDialogOpen(true);
    setNotesLoading(true);
    try {
      const response = await auditService.getReceiptAuditNotes(queueId);
      setReviewNotes(response.review_notes || null);
    } catch (error: any) {
      showToast.error(error?.message || 'Failed to load receipt notes');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async (note: string) => {
    const queueId = selectedAudit?.queue_id || selectedAudit?.id;
    if (!queueId) return;
    setNotesSaving(true);
    try {
      const response = await auditService.addReceiptAuditNote(queueId, note);
      setReviewNotes(response.review_notes || null);
      showToast.success('Note added');
      onNotesChanged?.();
    } catch (error: any) {
      showToast.error(error?.message || 'Failed to add note');
    } finally {
      setNotesSaving(false);
    }
  };

  return (
    <Box
      className="AuditsTable"
      sx={{ overflow: 'hidden' }}
    >
      <TableContainer className="at-table-container" sx={{ position: 'relative' }}>
        <Table
          size="small"
          sx={adminCombineSx(adminTableSx, {
            '& .MuiTableCell-root': { padding: '8px 12px !important' },
            '& .MuiTableHead-root .MuiTableCell-head': { textTransform: 'uppercase' },
            '& .MuiTableBody-root .MuiTableCell-body': { fontSize: '14px' },
            '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
              backgroundColor: adminColors.surfaceAlt,
            },
          })}
        >
          <TableHead>
            <TableRow className="at-table-head-row">
              <TableCell className="at-table-cell">Status</TableCell>
              <TableCell className="at-table-cell" sx={{ width: 64 }}>Notes</TableCell>

              <TableCell className="at-table-cell">Store Name</TableCell>
              <TableCell className="at-table-cell">Report Type</TableCell>
              <TableCell className="at-table-cell">Location Key</TableCell>
              <TableCell className="at-table-cell">
                <TableSortLabel
                  active={orderBy === 'shiftDateTime'}
                  direction={orderBy === 'shiftDateTime' ? order : 'asc'}
                  onClick={() => onRequestSort('shiftDateTime')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: orderBy === 'shiftDateTime' ? '#222B45 !important' : '#9AA3AF !important',
                      opacity: 1,
                    },
                    color: '#222B45',
                  }}
                >
                  Shift Date/Time
                </TableSortLabel>
              </TableCell>
              <TableCell className="at-table-cell">
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => onRequestSort('createdAt')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: orderBy === 'createdAt' ? '#222B45 !important' : '#9AA3AF !important',
                      opacity: 1,
                    },
                    color: '#222B45',
                  }}
                >
                  Created At
                </TableSortLabel>
              </TableCell>
              <TableCell className="at-table-cell">
                <TableSortLabel
                  active={orderBy === 'processedAt'}
                  direction={orderBy === 'processedAt' ? order : 'asc'}
                  onClick={() => onRequestSort('processedAt')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: orderBy === 'processedAt' ? '#222B45 !important' : '#9AA3AF !important',
                      opacity: 1,
                    },
                    color: '#222B45',
                  }}
                >
                  Processed At
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audits.length > 0 ? (
              audits.map((audit: ReceiptAudit, index: number) => {
                const storeName = audit.store_name || 'N/A';
                const reportTypeLabel = formatReportType(
                  (audit as any)?.report_type
                  || (audit as any)?.parsed_data?.detected_report_type
                  || (audit as any)?.parsed_data?.final_json?.report_type,
                );
                const locationKey = (audit as any)?.parsed_data?.final_json?.location_key || '-';
                const shiftDate = formatShiftDateForDisplay(getReceiptAuditShiftDate(audit));
                const shiftTime = formatShiftTimeForDisplay(getReceiptAuditShiftTime(audit));
                const createdAt = splitFormattedDateTime(formatReceiptAuditTimestamp(audit.created_at));
                const processedAt = splitFormattedDateTime(
                  (audit as any).updated_at ? formatReceiptAuditTimestamp((audit as any).updated_at) : 'N/A',
                );
                const status: string = audit.status;
                const statusLabel = formatReceiptAuditStatusLabel(status);
                const notePreview = getLatestNotePreview(audit.review_notes);
                const hasNotes = Boolean(notePreview);
                return (
                  <React.Fragment key={audit.queue_id || audit.id}>
                    <TableRow
                      hover
                      className={`${index % 2 === 0 ? 'at-row' : 'at-row-alt'}${notePreview ? ' at-row-with-note' : ''}`}
                      sx={{
                        "&:last-of-type td, &:last-of-type th": { border: 0 },
                        "& > td": notePreview ? { borderBottom: 0, pb: 0.25 } : undefined,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        onFixOCR(
                          audit.queue_id || audit.id || '',
                          audit.store_id || '',
                          isReceiptAuditProcessedStatus(audit.status),
                        )
                      }

                    >
                      <TableCell className="at-table-cell">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 132 }}>
                          <ReceiptAuditStatusIcon status={status} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#2B3445',
                              fontSize: '12px',
                              fontWeight: 800,
                              letterSpacing: 0,
                              textTransform: 'uppercase',
                              lineHeight: 1.1,
                            }}
                          >
                            {statusLabel}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell className="at-table-cell">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 28,
                            lineHeight: 1,
                          }}
                        >
                          <Tooltip title={hasNotes ? 'View notes' : 'Add note'}>
                            <IconButton
                              size="small"
                              onClick={(event) => handleOpenNotes(event, audit)}
                              sx={{
                                color: hasNotes ? '#2C6CA0' : '#AAB2C0',
                                p: 0.25,
                              }}
                              aria-label={hasNotes ? 'View receipt notes' : 'Add receipt note'}
                            >
                              <StickyNote2OutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {notePreview?.relativeTimestamp ? (
                            <Typography
                              component="span"
                              title={notePreview.timestamp}
                              sx={{
                                color: '#6B7280',
                                fontSize: 10,
                                fontWeight: 600,
                                lineHeight: 1,
                                mt: 0.1,
                              }}
                            >
                              {notePreview.relativeTimestamp}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>

                      <TableCell className="at-table-cell">
                        <Typography
                          variant="body2"
                            sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: notePreview ? 1.1 : undefined,
                            }}
                          color="#222B45"
                        >
                          {storeName}
                        </Typography>
                      </TableCell>
                      <TableCell className="at-table-cell">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 400, fontSize: "14px" }}
                          color="#222B45"
                        >
                          {reportTypeLabel}
                        </Typography>
                      </TableCell>
                      <TableCell className="at-table-cell">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 400, fontSize: "14px" }}
                          color="#222B45"
                        >
                          {locationKey}
                        </Typography>
                      </TableCell>
                      <TableCell className="at-table-cell">
                        <DateTimeCell date={shiftDate} time={shiftTime} />
                      </TableCell>
                      <TableCell className="at-table-cell">
                        <DateTimeCell date={createdAt.date} time={createdAt.time} />
                      </TableCell>
                      <TableCell className="at-table-cell">
                        <DateTimeCell date={processedAt.date} time={processedAt.time} />
                      </TableCell>
                    </TableRow>
                    {notePreview && (
                      <TableRow className={`${index % 2 === 0 ? 'at-row' : 'at-row-alt'} at-note-preview-row`}>
                        <TableCell
                          className="at-table-cell"
                          colSpan={2}
                          sx={{ pt: 0, pb: 0.25, borderTop: 0, borderBottom: 0 }}
                        />
                        <TableCell
                          className="at-table-cell"
                          colSpan={6}
                          sx={{
                            pt: 0,
                            pb: 0.25,
                            borderTop: 0,
                            borderBottom: 0,
                            cursor: 'pointer',
                          }}
                          onClick={(event) => handleOpenNotes(event, audit)}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              minWidth: 0,
                              color: '#6B7280',
                              fontSize: 12,
                              lineHeight: 1.1,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 12,
                                color: '#6B7280',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                              }}
                            >
                              {notePreview.body}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  {/* No data message left to parent */}
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, zIndex: 10, height: '100%' }}>
                <TableCell colSpan={8} align="center" sx={{ p: 0, m: 0, border: 0, background: 'rgba(255,255,255,0.7)', position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                    {/* Spinner provided by parent */}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 200]}
        component="div"
        count={total}
        rowsPerPage={size}
        page={page - 1}
        onPageChange={(event, newPage) => {
          onPageChange(event, newPage + 1);
        }}
        onRowsPerPageChange={onRowsPerPageChange}
      />
      <ReceiptNotesDialog
        open={notesDialogOpen}
        reviewNotes={reviewNotes}
        loading={notesLoading}
        saving={notesSaving}
        onClose={() => setNotesDialogOpen(false)}
        onAddNote={handleAddNote}
      />
    </Box>
  );
};

export default AuditsTable;
